// Service to fetch accounting data directly from backend APIs
import axiosInstance from '../utils/axios';

class BackendAccountingService {
  constructor() {
    this.baseURL = '/api';
  }

  // Fetch all financial data from backend
  async fetchFinancialData(schoolID) {
    try {
      console.log('🔍 Fetching financial data from backend for school:', schoolID);
      
      // Fetch all data in parallel
      const [studentsResponse, teachersResponse, expensesResponse, salesResponse, assetsResponse, classesResponse] = await Promise.allSettled([
        this.fetchStudents(schoolID),
        this.fetchTeachers(schoolID),
        this.fetchExpenses(schoolID),
        this.fetchSales(schoolID),
        this.fetchAssets(schoolID),
        this.fetchClasses(schoolID)
      ]);

      const data = {
        students: studentsResponse.status === 'fulfilled' ? studentsResponse.value : [],
        teachers: teachersResponse.status === 'fulfilled' ? teachersResponse.value : [],
        expenses: expensesResponse.status === 'fulfilled' ? expensesResponse.value : [],
        sales: salesResponse.status === 'fulfilled' ? salesResponse.value : [],
        assets: assetsResponse.status === 'fulfilled' ? assetsResponse.value : [],
        classes: classesResponse.status === 'fulfilled' ? classesResponse.value : []
      };

      console.log('📊 Backend data fetched:', {
        students: data.students.length,
        teachers: data.teachers.length,
        expenses: data.expenses.length,
        sales: data.sales.length,
        assets: data.assets.length,
        classes: data.classes.length
      });

      return data;
    } catch (error) {
      console.error('❌ Error fetching financial data:', error);
      throw error;
    }
  }

  // Fetch students data
  async fetchStudents(schoolID) {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/students/getstudentdata`, {
        schoolID: schoolID
      });
      return response.data.students || [];
    } catch (error) {
      console.error('Error fetching students:', error);
      return [];
    }
  }

  // Fetch teachers data
  async fetchTeachers(schoolID) {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/teachers/getworkersdata`, {
        schoolID: schoolID
      });
      return response.data.teachers || [];
    } catch (error) {
      console.error('Error fetching teachers:', error);
      return [];
    }
  }

  // Fetch expenses data
  async fetchExpenses(schoolID) {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/expenditure/getexpensesdata`, {
        schoolID: schoolID
      });
      return response.data.expenses || [];
    } catch (error) {
      console.error('Error fetching expenses:', error);
      return [];
    }
  }

  // Fetch sales data
  async fetchSales(schoolID) {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/sales/getsalesdata`, {
        schoolID: schoolID
      });
      return response.data.sales || [];
    } catch (error) {
      console.error('Error fetching sales:', error);
      return [];
    }
  }

  // Fetch assets data
  async fetchAssets(schoolID) {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/assets/getassetsdata`, {
        schoolID: schoolID
      });
      return response.data.assets || [];
    } catch (error) {
      console.error('Error fetching assets:', error);
      return [];
    }
  }

  // Fetch classes data
  async fetchClasses(schoolID) {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/class/getclassdata`, {
        schoolID: schoolID
      });
      return response.data.classes || [];
    } catch (error) {
      console.error('Error fetching classes:', error);
      return [];
    }
  }

  // Calculate financial metrics from backend data
  calculateFinancialMetrics(data) {
    const { students, teachers, expenses, sales, assets, classes } = data;

    // Calculate student finance metrics
    const studentFinance = this.calculateStudentFinanceMetrics(students, classes);
    
    // Calculate canteen fees metrics
    const canteenFees = this.calculateCanteenFeesMetrics(students);
    
    // Calculate salaries metrics
    const salaries = this.calculateSalariesMetrics(teachers);
    
    // Calculate expenditure metrics
    const expenditure = this.calculateExpenditureMetrics(expenses);
    
    // Calculate sales metrics
    const salesMetrics = this.calculateSalesMetrics(sales);
    
    // Calculate assets metrics
    const assetsMetrics = this.calculateAssetsMetrics(assets);

    // Calculate totals
    const totalRevenue = studentFinance.paidFees + canteenFees.totalRevenue + salesMetrics.totalRevenue;
    const totalExpenses = expenditure.totalExpenses + salaries.totalSalaries;
    const netIncome = totalRevenue - totalExpenses;
    const cashBalance = 50000 + totalRevenue - totalExpenses; // Base cash + revenue - expenses

    console.log('💰 Calculated financial metrics:', {
      totalRevenue,
      totalExpenses,
      netIncome,
      cashBalance,
      studentFinance,
      canteenFees,
      salesMetrics
    });

    return {
      keyMetrics: {
        totalRevenue,
        totalExpenses,
        netIncome,
        cashBalance
      },
      studentFinance,
      canteenFees,
      salaries,
      expenditure,
      sales: salesMetrics,
      assets: assetsMetrics
    };
  }

  // Calculate student finance metrics
  calculateStudentFinanceMetrics(students, classData = []) {
    console.log('📚 Calculating student finance metrics from:', students.length, 'students');
    console.log('🏫 Class data available:', classData.length, 'classes');
    
    let totalFees = 0;
    let paidFees = 0;
    let outstandingFees = 0;
    let paidStudents = 0;
    let outstandingStudents = 0;

    students.forEach((student, index) => {
      // Get class fees for this student
      const classMatch = classData.filter(cls => cls.classname === student.class_);
      const classFees = classMatch.length > 0 ? parseFloat(classMatch[0].classfees) || 0 : 0;
      
      if (student.finance && student.finance.length > 0) {
        const latestFinance = student.finance[student.finance.length - 1];
        
        // Calculate total fees
        const totalFeesAmount = parseFloat(latestFinance.totalfees) || 0;
        const arrears = parseFloat(latestFinance.Arrears) || 0;
        const otherCharges = parseFloat(latestFinance.othercharges) || 0;
        
        const totalStudentFees = totalFeesAmount > 0 ? totalFeesAmount : (classFees + arrears + otherCharges);
        totalFees += totalStudentFees;
        
        // Calculate paid amount
        if (latestFinance.payment && latestFinance.payment.length > 0) {
          const totalPaid = latestFinance.payment.reduce((sum, payment) => {
            if (payment.des !== "Discount") {
              return sum + (parseFloat(payment.amountpaid) || 0);
            }
            return sum;
          }, 0);
          paidFees += totalPaid;
        }
        
        // Calculate outstanding
        const balance = parseFloat(latestFinance.balance) || 0;
        outstandingFees += balance;

        if (balance === 0) {
          paidStudents++;
        } else {
          outstandingStudents++;
        }
      } else {
        // Student has no finance data, use class fees
        totalFees += classFees;
        outstandingFees += classFees;
        outstandingStudents++;
      }
    });

    return {
      totalFees,
      paidFees,
      outstandingFees,
      paidStudents,
      outstandingStudents
    };
  }

  // Calculate canteen fees metrics
  calculateCanteenFeesMetrics(students) {
    let totalRevenue = 0;
    let outstandingAmount = 0;

    students.forEach(student => {
      if (student.canteen && student.canteen.length > 0) {
        const latestCanteen = student.canteen[student.canteen.length - 1];
        totalRevenue += parseFloat(latestCanteen.totalAmount) || 0;
        outstandingAmount += parseFloat(latestCanteen.balance) || 0;
      }
    });

    return {
      totalRevenue,
      outstandingAmount
    };
  }

  // Calculate salaries metrics
  calculateSalariesMetrics(teachers) {
    let totalSalaries = 0;

    teachers.forEach(teacher => {
      if (teacher.salary) {
        totalSalaries += parseFloat(teacher.salary) || 0;
      }
    });

    return {
      totalSalaries
    };
  }

  // Calculate expenditure metrics
  calculateExpenditureMetrics(expenses) {
    let totalExpenses = 0;

    expenses.forEach(expense => {
      if (expense.expenses && expense.expenses.compile) {
        const compile = Array.isArray(expense.expenses.compile) 
          ? expense.expenses.compile 
          : [expense.expenses.compile];
        
        compile.forEach(item => {
          totalExpenses += parseFloat(item.amount) || 0;
        });
      }
    });

    return {
      totalExpenses
    };
  }

  // Calculate sales metrics
  calculateSalesMetrics(sales) {
    let totalRevenue = 0;

    sales.forEach(sale => {
      totalRevenue += parseFloat(sale.totalAmount) || 0;
    });

    return {
      totalRevenue
    };
  }

  // Calculate assets metrics
  calculateAssetsMetrics(assets) {
    let totalValue = 0;
    let totalDepreciation = 0;

    assets.forEach(asset => {
      totalValue += parseFloat(asset.currentValue) || 0;
      totalDepreciation += parseFloat(asset.depreciation) || 0;
    });

    return {
      totalValue,
      totalDepreciation
    };
  }

  // Get dashboard data using backend APIs
  async getDashboardData(schoolID) {
    try {
      const financialData = await this.fetchFinancialData(schoolID);
      const metrics = this.calculateFinancialMetrics(financialData);
      
      return {
        ...metrics,
        dataSummary: {
          students: financialData.students.length,
          teachers: financialData.teachers.length,
          expenses: financialData.expenses.length,
          sales: financialData.sales.length,
          assets: financialData.assets.length,
          classes: financialData.classes.length
        }
      };
    } catch (error) {
      console.error('Error getting dashboard data:', error);
      throw error;
    }
  }
}

export default new BackendAccountingService();
